import { isFutureEntry } from "./entryDate";
import {
  selectWeighedEntries,
  resolveBaseWeight,
  calcTotalLoss,
  calcTherapyWeek,
  resolveTherapyStart,
} from "./therapyStats";

export interface ChatEntry {
  // A D2 döntés óta hiányozhat (dózis-only bejegyzés) — `null` a drótról.
  weight?: number | null;
  dose?: number;
  date?: string;
  createdAt?: { seconds: number };
}

export interface ChatProfil {
  gyogyszer?: string;
  kezdoSuly?: string;
  celSuly?: string;
  terapiaKezdete?: string;
}

// A tényleges rendszer-prompt szövegét (instrukciók, szabályok) a
// claudeProxy function rakja rá szerveroldalon — a kliens csak ezeket a
// típusellenőrzött adatmezőket küldi, nem a végleges promptot (2026-08-12
// audit: korábban a kliens által épített teljes system-string ment ki,
// amit a function felül tudott volna íratni magával).
export function buildChatContext(
  rawEntries: ChatEntry[],
  profil: ChatProfil,
  platform: "native" | "web"
) {
  // Jövőbeli dátumú bejegyzés (elgépelt évszám/nap) kizárva a kontextusból —
  // az AI ne higgye "jelenlegi súlynak"/"utolsó dózisnak" azt, ami még nem
  // történt meg (2026-08-17 QA-lelet).
  const entries = rawEntries.filter(e => !isFutureEntry(e));
  // Az utolsó MÉRT súly, nem az utolsó bejegyzés súlya: egy súly nélküli
  // injekció-bejegyzés (D2) különben "nincs jelenlegi súly"-ként menne ki
  // a promptba, és az asszisztens a saját naplózott adatot tagadná le.
  const weighedEntries = selectWeighedEntries(entries);
  const lastWeight = weighedEntries[weighedEntries.length - 1]?.weight ?? null;
  // ⚠️ A7 (külső audit, 2026-10-14): a bázis, a fogyás és a terápiahét a
  // Főoldallal AZONOS kanonikus függvényekből jön (10. szabály). Korábban az
  // első bejegyzés volt a bázis és saját hét-képlet futott — profil kezdősúly
  // 100, első bejegyzés 98, jelenlegi 90 mellett az AI 8 kg-ot kapott, a
  // Főoldal 10-et mutatott, és ugyanabban a payloadban a `kezdoSuly` 100 volt.
  const kezdoSuly = resolveBaseWeight(profil.kezdoSuly, weighedEntries[0]?.weight, true);
  const loss = calcTotalLoss(kezdoSuly, lastWeight);
  const totalLoss = loss != null ? Number(loss.toFixed(1)) : null;
  const doseEntries = entries.filter(e => e.dose && e.dose > 0);
  const lastDose = doseEntries[doseEntries.length - 1]?.dose ?? null;
  const therapyStart = resolveTherapyStart(profil.terapiaKezdete, entries);
  const therapyWeek = therapyStart ? calcTherapyWeek(therapyStart) : null;
  const celSuly = profil.celSuly ? parseFloat(profil.celSuly) : null;

  return {
    // Platform-jelző: a szerveroldali prompt natívon szigorúbb ágat kap
    // (App Store 1.4.2 / APL531603). Típusellenőrzött enum-mező, a meglévő
    // minta szerint — a kliens továbbra sem küld szöveges promptot.
    platform,
    gyogyszer: profil.gyogyszer ?? null,
    terapiaKezdete: profil.terapiaKezdete ?? null,
    terapiaHet: therapyWeek,
    kezdoSuly: Number.isFinite(kezdoSuly as number) ? kezdoSuly : null,
    jelenlegiSuly: lastWeight,
    osszesFogyas: totalLoss,
    celSuly: Number.isFinite(celSuly as number) ? celSuly : null,
    jelenlegiDozis: lastDose,
    bejegyzesekSzama: entries.length,
    utolsoBejegyzesek: entries.slice(-5).map(e => ({
      date: e.date ?? null,
      weight: e.weight ?? null,
      dose: e.dose ?? null,
    })),
  };
}
